// NuevoRiesgoModal.jsx
import React, { useEffect, useMemo } from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Space,
    Button,
} from "antd";
import dayjs from "dayjs";

const { TextArea } = Input;

// Opciones demo (puedes inyectarlas por props si las tienes desde un servicio)
const TIPOS_RIESGO = [
    "Riesgos Operacionales (DPS)",
    "Riesgos de la Naturaleza (DPS)",
    "Riesgos Económicos (DPS)",
    "Riesgos Regulatorios (DPS)",
    "Riesgos Financieros (DPS)",
    "Riesgo Logístico - Alimentos Perecederos o Complementos Agrícolas (DPS)",
    "Logísticos y de cadena de suministro",
    "Transporte y accesos viales",
    "Seguridad/orden público",
    "Regulatorios y sanitarios",
    "Afluencia y orden de entrega",
    "Integridad del producto",
    "Accidentes y salud",
    "Desviación/mercado secundario",
    "Quejas y reclamos (PQRS)",
    "Auditoría y cumplimiento",
    "Reputacional",
];

const ESTADOS = ["En curso", "Implementado", "Cerrado"];
const RESPONSABLES = [
    "Coordinador General",
    "Coordinador Municipal",
    "Líder de logística y Compras",
    "Comunicador",
    "Promotor de Hábitos y Estilos de Vida Saludable",
    "472",
    "472/ Líder de logística y Compras",
];
const PROVEEDORES = ["Proveedor A", "Proveedor B", "Proveedor C"];

// Demostración de Departamentos/Municipios
const DEPTO_MUNI = [
    { dpto: "Cauca", municipios: ["Popayán", "Santander de Quilichao", "Piendamó"] },
    { dpto: "Valle del Cauca", municipios: ["Cali", "Palmira", "Buga"] },
    { dpto: "Putumayo", municipios: ["Mocoa", "Puerto Asís", "Orito"] },
];

const NuevoRiesgoModal = ({
    open,
    onClose,
    onSubmit,         // (values) => void
    initialValues,    // opcional para edición
    tipos = TIPOS_RIESGO,
    estados = ESTADOS,
    responsables = RESPONSABLES,
    proveedores = PROVEEDORES,
    deptoMuni = DEPTO_MUNI,
}) => {
    const [form] = Form.useForm();

    // Municipios disponibles según el departamento seleccionado
    const dep = Form.useWatch("departamento", form);
    const municipiosDisponibles = useMemo(() => {
        const found = deptoMuni.find((x) => x.dpto === dep);
        return found ? found.municipios : [];
    }, [dep, deptoMuni]);

    // Calcular días entre fechas para "Días para solución"
    const fechaInicio = Form.useWatch("fechaInicio", form);
    const fechaSolucion = Form.useWatch("fechaSolucion", form);

    useEffect(() => {
        if (fechaInicio && fechaSolucion) {
            const d1 = dayjs(fechaInicio);
            const d2 = dayjs(fechaSolucion);
            const diff = d2.diff(d1, "day");
            form.setFieldsValue({ diasSolucion: diff >= 0 ? diff : 0 });
        }
    }, [fechaInicio, fechaSolucion]); // eslint-disable-line

    // Reset y carga de initial values cuando se abre
    useEffect(() => {
        if (!open) return;
        form.resetFields();
        if (initialValues) {
            const patched = {
                ...initialValues,
                fechaInicio: initialValues.fechaInicio ? dayjs(initialValues.fechaInicio) : null,
                fechaSolucion: initialValues.fechaSolucion ? dayjs(initialValues.fechaSolucion) : null,
            };
            form.setFieldsValue(patched);
        }
    }, [open]); // eslint-disable-line

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                fechaInicio: values.fechaInicio ? values.fechaInicio.format("YYYY-MM-DD") : null,
                fechaSolucion: values.fechaSolucion ? values.fechaSolucion.format("YYYY-MM-DD") : null,
            };
            onSubmit?.(payload);
            form.resetFields();
        } catch {
            // antd ya muestra los errores
        }
    };

    return (
        <Modal
            title="Registrar nuevo riesgo"
            open={open}
            onCancel={onClose}
            width={"80%"}
            destroy
            style={{ top: "10px" }}

            footer={
                <Space>
                    <Button onClick={() => form.resetFields()}>Limpiar</Button>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button type="primary" onClick={handleOk}>
                        Guardar
                    </Button>
                </Space>
            }
        >
            <Form layout="vertical" form={form}>
                <Row gutter={[16, 8]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="tipo"
                            label="Tipo de riesgo"
                            rules={[{ required: true, message: "Selecciona el tipo de riesgo" }]}
                        >
                            <Select
                                showSearch
                                placeholder="Selecciona un tipo"
                                options={tipos.map((t) => ({ value: t, label: t }))}
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="estado"
                            label="Estado"
                            rules={[{ required: true, message: "Selecciona el estado" }]}
                        >
                            <Select
                                placeholder="Selecciona un estado"
                                options={estados.map((e) => ({ value: e, label: e }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="descripcion"
                            label="Descripción del riesgo"
                            rules={[
                                { required: true, message: "Describe el riesgo" },
                                { max: 1000, message: "Máximo 1000 caracteres" },
                            ]}
                        >
                            <TextArea rows={3} placeholder="Paros, huelgas, asonadas..." />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item name="especificar" label="Especificar (opcional)">
                            <Input placeholder="Detalles adicionales del riesgo" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="responsable"
                            label="Responsable"
                            rules={[{ required: true, message: "Selecciona el responsable" }]}
                        >
                            <Select
                                showSearch
                                placeholder="Selecciona un responsable"
                                options={responsables.map((r) => ({ value: r, label: r }))}
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="quienReporta" label="Quien reporta">
                            <Input placeholder="Nombre de quien reporta" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="proveedor" label="Proveedor">
                            <Select
                                allowClear
                                placeholder="Selecciona proveedor"
                                options={proveedores.map((p) => ({ value: p, label: p }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="departamento" label="Departamento">
                            <Select
                                allowClear
                                showSearch
                                placeholder="Selecciona un departamento"
                                options={deptoMuni.map((d) => ({ value: d.dpto, label: d.dpto }))}
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="municipio" label="Municipio">
                            <Select
                                allowClear
                                showSearch
                                placeholder="Selecciona un municipio"
                                options={municipiosDisponibles.map((m) => ({ value: m, label: m }))}
                                optionFilterProp="label"
                                disabled={!dep}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="observaciones" label="Observaciones">
                            <TextArea rows={2} placeholder="Notas, decisiones, responsables de acción..." />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="fechaInicio" label="Fecha inicio">
                            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="fechaSolucion" label="Fecha solución">
                            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="diasSolucion" label="Días para solución">
                            <Input type="number" min={0} placeholder="Se calcula por fechas, editable" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default NuevoRiesgoModal;
